interface BarLabelProps {
    x?: number | string;
    y?: number | string;
    width?: number | string;
    height?: number | string;
    value?: number | string | null;
    payload?: {
        isSpacer?: boolean;
        [key: string]: any;
    };
    formatter?: (value: number) => string;
}

export const BarValueLabel = ({
    x = 0,
    y = 0,
    width = 0,
    height = 0,
    value,
    payload,
    formatter,
}: BarLabelProps) => {
    if (payload?.isSpacer || value == null) {
        return null;
    }

    const numericX = Number(x);
    const numericY = Number(y);
    const numericWidth = Number(width);
    const numericHeight = Number(height);
    const numericValue = Number(value);

    const label = formatter
        ? formatter(numericValue)
        : numericValue.toLocaleString();

    return (
        <text
            x={numericX + numericWidth + 6}
            y={numericY + numericHeight / 2}
            dy="0.35em"
            fill="#475569"
            fontSize={10}
            fontWeight={600}
            textAnchor="start"
            pointerEvents="none"
        >
            {label}
        </text>
    );
};

interface BarPercentageLabelProps extends BarLabelProps {
    total?: number;
}

export const BarPercentageLabel = ({
    x = 0,
    y = 0,
    width = 0,
    height = 0,
    value,
    payload,
    total,
    formatter,
}: BarPercentageLabelProps) => {
    if (payload?.isSpacer || value == null) {
        return null;
    }

    const numericX = Number(x);
    const numericY = Number(y);
    const numericWidth = Number(width);
    const numericHeight = Number(height);
    const numericValue = Number(value);

    // Some charts already store a percentage as the value.
    // Other charts need to calculate percentage from a total.
    const label = formatter
        ? formatter(numericValue)
        : total && total > 0
            ? `${((numericValue / total) * 100).toFixed(0)}%`
            : null;

    if (!label) {
        return null;
    }

    const minimumWidth = label.length * 7 + 12;

    if (numericWidth < minimumWidth) {
        return null;
    }

    return (
        <text
            x={numericX + 7}
            y={numericY + numericHeight / 2}
            dy="0.35em"
            fill="#ffffff"
            fontSize={10}
            fontWeight={600}
            textAnchor="start"
            pointerEvents="none"
        >
            {label}
        </text>
    );
};

interface CustomTooltipBarProps {
    active?: boolean;
    payload?: any[];
    label?: string;
    total?: number;
    valueFormatter?: (value: number) => string;
    labelFormatter?: (label: string) => string;
    valueLabel?: string;
    countKey?: string;
    shareKey?: string;
    shareFormatter?: (value: number) => string;
}

export const CustomTooltipBar = ({
    active,
    payload,
    label,
    total,
    valueFormatter,
    labelFormatter,
    valueLabel = "Count",
    countKey,
    shareKey,
    shareFormatter,
}: CustomTooltipBarProps) => {
    if (!active || !payload?.length) {
        return null;
    }

    const entry = payload[0];
    const barData = entry.payload;

    if (barData?.isSpacer) {
        return null;
    }

    const rawValue = Number(entry.value);

    // Normally the bar value is also the displayed value.
    // countKey allows a chart to store the count separately.
    const displayValue = countKey
        ? Number(barData?.[countKey])
        : rawValue;

    const formattedValue = valueFormatter
        ? valueFormatter(displayValue)
        : displayValue.toLocaleString();

    const formattedLabel = labelFormatter
        ? labelFormatter(label ?? "")
        : label;

    // Either format the bar value directly as a share,
    // or calculate its share from a total.
    const shareValue = shareKey
        ? Number(barData?.[shareKey])
        : rawValue;

    const formattedShare = shareFormatter
        ? shareFormatter(shareValue)
        : total && total > 0
            ? `${((rawValue / total) * 100).toFixed(1)}%`
            : null;

    return (
        <div className="bg-white p-2 border border-slate-200 rounded shadow-sm text-xs min-w-[120px]">
            <p className="font-semibold mb-1">
                {formattedLabel}
            </p>

            <div className="flex justify-between gap-4">
                <span className="text-slate-500">{valueLabel}</span>
                <span className="font-medium">
                    {formattedValue}
                </span>
            </div>

            {formattedShare !== null && (
                <div className="flex justify-between gap-4">
                    <span className="text-slate-500">Share</span>
                    <span className="font-medium">
                        {formattedShare}
                    </span>
                </div>
            )}
        </div>
    );
};

export const CustomTick = ({
    x,
    y,
    payload,
    maxLineLength = 20,
}: any) => {
    const raw =
        typeof payload.value === "string"
            ? payload.value
            : String(payload.value);

    const label = raw
        .replace(/\u00A0/g, " ")
        .replace(/\s+/g, " ")
        .trim();

    const words = label.split(" ");
    const lines: string[] = [];

    let currentLine = "";

    words.forEach((word: any) => {
        const nextLine = currentLine
            ? `${currentLine} ${word}`
            : word;

        if (nextLine.length <= maxLineLength) {
            currentLine = nextLine;
        } else {
            if (currentLine) {
                lines.push(currentLine);
            }

            currentLine = word;
        }
    });

    if (currentLine) {
        lines.push(currentLine);
    }

    if (lines.length === 1) {
        return (
            <text
                x={x}
                y={y}
                dy={2}
                textAnchor="end"
                fill="#666"
                style={{
                    whiteSpace: "nowrap",
                    fontSize: 10,
                }}
            >
                {lines[0]}
            </text>
        );
    }

    return (
        <text
            x={x}
            y={y}
            textAnchor="end"
            fill="#666"
            style={{ fontSize: 10 }}
        >
            {lines.map((line, index) => (
                <tspan
                    key={`${line}-${index}`}
                    x={x}
                    dy={index === 0 ? -1 : 9}
                >
                    {line}
                </tspan>
            ))}
        </text>
    );
};